<template>
  <div class="animated fadeIn col-md-12">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            ایجاد
            <strong v-text="$lang.template"></strong>
          </div>

          <LoadingSpinner :loading="isLoading" :text="$lang.sending_data" />
          <b-form @submit.prevent="save" v-show="!isLoading">
            <b-form-group
              :label="$lang.caption"
              label-for="caption"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.caption"
                :rules="$v.caption.$params"
              />
              <b-form-input
                id="caption"
                type="text"
                :autocomplete="$lang.caption"
                :state="$v.caption.$dirty ? !$v.caption.$error : null"
                v-model="$v.caption.$model"
              ></b-form-input>
              <b-form-invalid-feedback id="caption-feedback"
                >{{ $lang.error_required | replace($lang.caption) }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              :label="$lang.indicatorCategoryRatioControl"
              label-for="indicatorCategoryRatioControl"
              :label-cols="this.$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.indicatorCategoryRatioControl"
                :rules="$v.indicatorCategoryRatioControl.$params"
              />
              <selectAsync
                :options="indicatorCategoryRatioControlOptions"
                v-model="$v.indicatorCategoryRatioControl.$model"
                label="caption"
                :isValid="
                  $v.indicatorCategoryRatioControl.$dirty
                    ? !$v.indicatorCategoryRatioControl.$error
                    : null
                "
                :loading="indicatorCategoryRatioControlLoading"
                :asyncFind="getIndicatorCategoryRatioControlOptions"
                @input="fillIndicatorCategoryRatioControl"
              />

              <b-form-invalid-feedback
                :style="{
                  display: $v.indicatorCategoryRatioControl.$error
                    ? 'block'
                    : 'none',
                }"
              >
                {{
                  $lang.error_required
                    | replace($lang.indicatorCategoryRatioControl)
                }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              :label="$lang.indicatorCategoryRatioControlValue"
              label-for="indicatorCategoryRatioControlValue"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.indicatorCategoryRatioControlValue"
                :rules="$v.indicatorCategoryRatioControlValue.$params"
              />
              <b-form-input
                id="indicatorCategoryRatioControlValue"
                type="number"
                step="0.01"
                :autocomplete="$lang.indicatorCategoryRatioControlValue"
                :state="
                  $v.indicatorCategoryRatioControlValue.$dirty
                    ? !$v.indicatorCategoryRatioControlValue.$error
                    : null
                "
                v-model="$v.indicatorCategoryRatioControlValue.$model"
              ></b-form-input>
              <b-form-invalid-feedback
                id="indicatorCategoryRatioControlValue-feedback"
                >{{
                  $lang.error_required
                    | replace($lang.indicatorCategoryRatioControlValue)
                }}
              </b-form-invalid-feedback>
            </b-form-group>
            <TagComponent
              category_name="template_level"
              :label="$lang.level"
              :parentPermissions="permissions"
              :parentValue="level_selected"
              :requiredLabel="levelSelectedRequired"
              :checkValidation="levelSelectedCheckValidation"
              @updateCheckValidation="levelSelectedUpdateCheckValidation"
              @getSelected="getLevelSelected"
            />
            <b-form-group
              :label="$lang.company"
              label-for="company_id"
              :label-cols="this.$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.company"
                :rules="$v.company_id.$params"
              />
              <selectAsync
                :options="company_options"
                v-model="$v.company_id.$model"
                label="text"
                :isValid="$v.company_id.$dirty ? !$v.company_id.$error : null"
                :loading="companyLoading"
                :asyncFind="getCompanies"
              />

              <b-form-invalid-feedback
                :style="{
                  display: $v.company_id.$error ? 'block' : 'none',
                }"
              >
                {{ $lang.error_required | replace($lang.company) }}
              </b-form-invalid-feedback>
            </b-form-group>
            <div slot="footer" class="form-group form-btn">
              <b-button
                type="reset"
                class="mr-2"
                variant="outline-danger"
                @click="close"
              >
                <mdicon name="close" size="20" />
                {{ $lang.cancelButton }}
              </b-button>
              <b-button
                type="submit"
                variant="success"
                v-if="isAdmin(permissions)"
              >
                <mdicon name="check" size="20" />
                {{ $lang.submitButton }}
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import TagComponent from "@/components/tag/tagComponent.vue";
import LoadingSpinner from "@/components/base/LoadingSpinner";
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { isAdmin, generateApiUrl, generateError } from "@/shared/utils";
import { getIndicatorCategoryCombo } from "@/services";

export default {
  components: {
    TagComponent,
    LoadingSpinner,
  },
  validations: {
    caption: {
      required,
    },
    indicatorCategoryRatioControl: {
      required,
    },
    indicatorCategoryRatioControlValue: {
      required,
    },
    company_id: {
      required,
    },
  },
  props: {
    permissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      caption: "",
      indicatorCategoryRatioControl: null,
      indicatorCategoryRatioControlOptions: [],
      indicatorCategoryRatioControlLoading: false,
      indicatorCategoryRatioControlValue: null,
      company_id: null,
      company_options: [],
      companyLoading: false,
      level_selected: null,
      levelSelectedRequired: false,
      levelSelectedCheckValidation: false,
    };
  },
  methods: {
    pathOr,
    isAdmin,
    generateApiUrl,
    close() {
      this.$emit("close");
    },
    async getIndicatorCategoryRatioControlOptions(text = null) {
      try {
        this.indicatorCategoryRatioControlLoading = true;
        const { options } = await getIndicatorCategoryCombo(text);
        this.indicatorCategoryRatioControlOptions = options;
      } catch (error) {
        generateError(error);
      } finally {
        this.indicatorCategoryRatioControlLoading = false;
      }
    },
    fillIndicatorCategoryRatioControl({ value }) {
      this.indicatorCategoryRatioControlValue = value;
    },
    async getCompanies(text = null) {
      if (!text) return;
      try {
        this.companyLoading = true;
        const url = generateApiUrl(["kanoon", "company_combo"], [], {
          search_txt: text,
        });
        const response = await this.$httpService.get(url);
        const data = pathOr([], ["data", "data"], response);
        this.company_options = data.map((item) => {
          return {
            value: pathOr(null, ["code"], item),
            text: pathOr("", ["name"], item),
          };
        });
      } catch (error) {
        generateError(error);
      } finally {
        this.companyLoading = false;
      }
    },
    async save() {
      this.levelSelectedCheckValidation = true;
      this.$v.$touch();
      if (this.$v.$anyError) return;
      if (this.levelSelectedRequired && !this.level_selected) return;
      try {
        this.isLoading = true;
        const url = generateApiUrl(["kanoon", "template"]);
        const payload = {
          caption: this.caption,
          indicator_category_ratio_control_id: pathOr(
            null,
            ["id"],
            this.indicatorCategoryRatioControl
          ),
          indicator_category_ratio_control_value:
            this.indicatorCategoryRatioControlValue,
          category_id: 1,
          level_id: pathOr(null, ["value"], this.level_selected),
          company_code: pathOr(null, ["value"], this.company_id),
        };
        const response = await this.$httpService.post(url, payload);
        this.$notify.success(response.data.message);
        this.close();
        this.$emit("getData");
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    levelSelectedUpdateCheckValidation(data) {
      this.levelSelectedCheckValidation = data;
    },
    getLevelSelected(data) {
      this.level_selected = data;
    },
  },
  mounted() {
    this.getIndicatorCategoryRatioControlOptions();
  },
};
</script>

<style scoped lang="css">
.btn {
  margin-bottom: 4px;
}
</style>
